import { useRef, useState } from "react";
import { FolderOpen, ChevronDown, ChevronUp, Upload, Paperclip, X, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import FileUploader from "@/components/FileUploader";

const BOXES = [
  { id: "git", name: "Goods in Transit insurance" },
  { id: "public_liability", name: "Public Liability insurance" },
  { id: "employers_liability", name: "Employers' Liability insurance" },
  { id: "fleet", name: "Fleet / Hire & Reward insurance" },
  { id: "agreement", name: "Agreement" },
  { id: "", name: "Other documents" },
  { id: "vat_check", name: "VAT check", vatOnly: true },
];

function DocBox({ box, contactId }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(null);
  const slug = box.id || "other";
  return (
    <div className="rounded-sm border border-slate-200 bg-white" data-testid={`contact-docbox-${slug}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`contact-docbox-toggle-${slug}`}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors">
        <span className="text-sm text-slate-800 inline-flex items-center gap-2">
          <FileText className="h-3.5 w-3.5 text-slate-500" />{box.name}
          {count !== null && <span className="text-xs text-slate-400">({count})</span>}
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
      </button>
      {open && (
        <div className="px-3 pb-3 border-t border-slate-100">
          <FileUploader entityType="contact" entityId={box.id ? `${contactId}__${box.id}` : contactId}
            label="Tick 'Has expiry' to set a date" showExpiry showDocNumber onChange={(fs) => setCount(fs.length)} />
        </div>
      )}
    </div>
  );
}

// Documents panel inside the subcontractor dialog. Before the contact exists, files are staged and uploaded after save.
export default function ContactDocs({ contactId, staged, setStaged, vatRegistered }) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  return (
    <div className="md:col-span-2">
      <button type="button" data-testid="contact-toggle-docs" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between rounded-sm border border-slate-200 px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors">
        <span className="text-sm font-medium text-slate-700 inline-flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-slate-500" /> Documents
          {!contactId && staged.length > 0 && <span className="text-xs text-slate-500">({staged.length})</span>}
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
      </button>
      {open && (
        <div className="mt-3" data-testid="contact-docs-panel">
          {contactId ? (
            <div className="space-y-2">
              {BOXES.filter((b) => !b.vatOnly || vatRegistered).map((b) => <DocBox key={b.id || "other"} box={b} contactId={contactId} />)}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-xs text-slate-500 px-1">Attach files now — they go into &quot;Other documents&quot; when you click Add. Insurance boxes, expiry dates and document numbers are available after saving.</div>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" className="rounded-sm" data-testid="contact-docs-attach" onClick={() => inputRef.current?.click()}>
                  <Upload className="h-3.5 w-3.5 mr-1.5" /> Attach files
                </Button>
                <input ref={inputRef} type="file" multiple className="hidden" data-testid="contact-docs-input"
                  onChange={(e) => { setStaged((s) => [...s, ...Array.from(e.target.files || []).map((file) => ({ file, name: file.name }))]); e.target.value = ""; }} />
                {staged.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-xs border border-slate-200 rounded-sm pl-2 pr-1 py-0.5 bg-white" data-testid={`contact-docs-staged-${i}`}>
                    <Paperclip className="h-3 w-3 shrink-0" />
                    <input value={f.name} title="Name this file" data-testid={`contact-docs-staged-name-${i}`}
                      onChange={(e) => setStaged((s) => s.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                      className="w-44 bg-transparent outline-none border-b border-dashed border-slate-300 focus:border-slate-600" />
                    <button type="button" onClick={() => setStaged((s) => s.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
